/* =============================================================
   LEXIQAMUS INNOVATIONS 2026
   ============================================================= */
'use strict';

/* -------------------------------------------------------------
   HOME VIEWPORT HEIGHT SYNC
   Fills the home block from its top edge to the bottom of the
   viewport so the first BuildLQ section does not peek through.
   ------------------------------------------------------------- */
const syncHomeViewportToWindowHeight = () => {
  const home = document.querySelector('.lexiqamus-home-viewport');
  if (!home) return;

  const applyHeight = () => {
    const topOffset = home.getBoundingClientRect().top;
    const height    = Math.max(320, Math.round(window.innerHeight - topOffset));
    home.style.minHeight = `${height}px`;
    home.style.height    = `${height}px`;
  };

  applyHeight();
  requestAnimationFrame(() => requestAnimationFrame(applyHeight));

  if (home.dataset.lexiqamusHomeResizeBound) return;
  home.dataset.lexiqamusHomeResizeBound = '1';
  window.addEventListener('resize', applyHeight);
  window.addEventListener('load',   applyHeight);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(applyHeight);
  }
};

/* -------------------------------------------------------------
   INTERFACE LAB — screenshot / feature card toggle
   ------------------------------------------------------------- */
const setLexiqamusLabMode = (selectedMode) => {
  document.querySelectorAll('#lexiqamus-innovations-root .lexiqamus-feature-toggle-button')
    .forEach((btn, idx) => btn.classList.toggle('active', idx === selectedMode));

  const resultsScreenshot  = document.getElementById('lexiqamusImgResults');
  const documentScreenshot = document.getElementById('lexiqamusImgPopup');
  const resultsInfoCard    = document.getElementById('lexiqamusCardResults');
  const documentInfoCard   = document.getElementById('lexiqamusCardPopup');

  if (selectedMode === 0) {
    resultsScreenshot?.classList.add('active');
    documentScreenshot?.classList.remove('active');
    resultsInfoCard?.classList.add('active');
    documentInfoCard?.classList.remove('active');
  } else {
    resultsScreenshot?.classList.remove('active');
    documentScreenshot?.classList.add('active');
    resultsInfoCard?.classList.remove('active');
    documentInfoCard?.classList.add('active');
  }
};

const handleScrollTarget = (el) => {
  const target = el.dataset.scrollTarget;
  if (target === 'top') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  const dest = document.getElementById(target);
  if (dest) dest.scrollIntoView({ behavior: 'smooth' });
};

/* Delegated UI for scroll arrows and lab toggles (CSP-safe; survives Turbolinks DOM swaps). */
if (!document.documentElement.dataset.lexiqamusLandingUiBound) {
  document.documentElement.dataset.lexiqamusLandingUiBound = '1';
  document.addEventListener('click', (event) => {
    const scrollTrigger = event.target.closest('[data-scroll-target]');
    if (scrollTrigger) {
      event.preventDefault();
      handleScrollTarget(scrollTrigger);
      return;
    }
    const labButton = event.target.closest('.lexiqamus-feature-toggle-button[data-lab-mode]');
    if (labButton) setLexiqamusLabMode(parseInt(labButton.dataset.labMode, 10));
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const scrollTrigger = event.target.closest('[data-scroll-target]');
    if (!scrollTrigger) return;
    event.preventDefault();
    handleScrollTarget(scrollTrigger);
  });
}

/* -------------------------------------------------------------
   MAIN INIT — called on DOMContentLoaded and turbolinks:load
   ------------------------------------------------------------- */
const initInnovationsLanding = () => {
  syncHomeViewportToWindowHeight();

  if (window.innovationsLandingInitialized) return;
  if (!document.getElementById('lexiqamus-innovations-root')) {
    document.documentElement.classList.remove('lexiqamus-innovations-scroll');
    return;
  }
  window.innovationsLandingInitialized = true;
  document.documentElement.classList.add('lexiqamus-innovations-scroll');

  /* -----------------------------------------------------------
     PROGRAM CARD ENTRANCE + FLOW SECTION TRIGGER
     Observes the hero program card (fade-in on enter) and the
     flow section (triggers the full scan scenario once).
     ----------------------------------------------------------- */
  const sectionVisibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.target.id !== 'lexiqamusProgramCard' && entry.target.id !== 'sectionFlow') return;

      if (entry.isIntersecting) {
        if (entry.target.id === 'lexiqamusProgramCard') {
          entry.target.classList.add('start-anim');
        }
        if (entry.target.id === 'sectionFlow' && !window.flowSectionScenarioStarted) {
          window.flowSectionScenarioStarted = true;
          playFlowSectionScenario();
        }
      } else if (entry.target.id === 'lexiqamusProgramCard') {
        entry.target.classList.remove('start-anim');
      }
    });
  }, { threshold: 0.3 });

  const programCard = document.getElementById('lexiqamusProgramCard');
  const flowSection = document.getElementById('sectionFlow');
  if (programCard) sectionVisibilityObserver.observe(programCard);
  if (flowSection)  sectionVisibilityObserver.observe(flowSection);

  /* -----------------------------------------------------------
     HERITAGE LIBRARY PARALLAX
     Moves the background image subtly on mouse movement.
     Only one listener is registered per page; it is removed on
     turbolinks:before-render to prevent accumulation.
     ----------------------------------------------------------- */
  const heritageParallaxHandler = (e) => {
    const bg = document.getElementById('lexiqamusHeritageBackground');
    if (!bg || bg.getBoundingClientRect().top >= window.innerHeight) return;
    const xShift = (window.innerWidth  / 2 - e.pageX) / 50;
    const yShift = (window.innerHeight / 2 - e.pageY) / 50;
    bg.style.transform = `translate(${xShift}px, ${yShift}px) scale(1.05)`;
  };
  document.addEventListener('mousemove', heritageParallaxHandler);
  document.addEventListener('turbolinks:before-render', () => {
    document.removeEventListener('mousemove', heritageParallaxHandler);
  }, { once: true });

  /* -----------------------------------------------------------
     FUTURE SECTION — ANIMATED NETWORK CANVAS
     Renders floating particles with connecting lines to suggest
     a semantic word network.
     ----------------------------------------------------------- */
  const networkCanvas = document.getElementById('lexiqamusNetworkCanvas');
  if (networkCanvas) {
    const networkCtx = networkCanvas.getContext('2d');
    let networkWidth, networkHeight;
    const networkParticles = [];

    const resizeNetworkCanvas = () => {
      networkWidth  = networkCanvas.width  = networkCanvas.offsetWidth;
      networkHeight = networkCanvas.height = networkCanvas.offsetHeight;
    };
    window.addEventListener('resize', resizeNetworkCanvas);
    resizeNetworkCanvas();

    class NetworkParticle {
      constructor() {
        this.x    = Math.random() * networkWidth;
        this.y    = Math.random() * networkHeight;
        this.vx   = (Math.random() - 0.5) * 0.5;
        this.vy   = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 2 + 1;
      }
      update() {
        this.x += this.vx; this.y += this.vy;
        if (this.x < 0 || this.x > networkWidth)  this.vx *= -1;
        if (this.y < 0 || this.y > networkHeight)  this.vy *= -1;
      }
      draw() {
        networkCtx.beginPath();
        networkCtx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        networkCtx.fillStyle = 'rgba(251,191,36,0.7)';
        networkCtx.fill();
      }
    }

    const particleCount = Math.min(80, Math.floor(window.innerWidth * 0.06));
    for (let i = 0; i < particleCount; i++) networkParticles.push(new NetworkParticle());

    let networkRafId;
    const drawNetworkFrame = () => {
      if (!document.contains(networkCanvas)) {
        cancelAnimationFrame(networkRafId);
        return;
      }
      networkCtx.clearRect(0, 0, networkWidth, networkHeight);
      networkParticles.forEach((particle, i) => {
        particle.update();
        particle.draw();
        for (let j = i + 1; j < networkParticles.length; j++) {
          const dx   = particle.x - networkParticles[j].x;
          const dy   = particle.y - networkParticles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            networkCtx.beginPath();
            networkCtx.strokeStyle = `rgba(255,255,255,${0.15 - dist / 1000})`;
            networkCtx.lineWidth   = 0.5;
            networkCtx.moveTo(particle.x, particle.y);
            networkCtx.lineTo(networkParticles[j].x, networkParticles[j].y);
            networkCtx.stroke();
          }
        }
      });
      networkRafId = requestAnimationFrame(drawNetworkFrame);
    };
    networkRafId = requestAnimationFrame(drawNetworkFrame);
    document.addEventListener('turbolinks:before-cache', () => {
      cancelAnimationFrame(networkRafId);
      window.removeEventListener('resize', resizeNetworkCanvas);
    }, { once: true });
  }

  /* -----------------------------------------------------------
     FLOW SECTION — DATA PARTICLE ANIMATION (canvas rain)
     Animates Ottoman characters flying from the original document
     card to the processed card while scanning.
     ----------------------------------------------------------- */
  const waitMilliseconds = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  let currentDataParticleColor  = '#06b6d4';
  let isDataCanvasAnimating     = false;
  let dataParticles             = [];
  let dataCanvasAnimationFrameId;

  const ottomanCharacterPool = ['حركة','سكوت','تلاش','عطالات','اراده','دوره','ا','ب','ج','د','٠','١','٢','X','Y','0','1'];
  const dataFlowCanvas       = document.getElementById('lexiqamus-data-canvas');
  const dataFlowContext      = dataFlowCanvas ? dataFlowCanvas.getContext('2d') : null;

  const resizeDataFlowCanvas = () => {
    if (!dataFlowCanvas) return;
    dataFlowCanvas.width  = dataFlowCanvas.offsetWidth;
    dataFlowCanvas.height = dataFlowCanvas.offsetHeight;
  };
  window.addEventListener('resize', resizeDataFlowCanvas);
  resizeDataFlowCanvas();

  class DataFlowParticle {
    constructor() { this.reset(); this.progress = Math.random(); }
    reset() {
      const originCard = document.getElementById('lexiqamusCardOriginal');
      const targetCard = document.getElementById('lexiqamusCardProcessed');
      if (!originCard || !targetCard) {
        // Cards not yet in the DOM — mark inactive with safe defaults so draw() is a no-op.
        this.active  = false;
        this.startX  = 0; this.startY  = 0;
        this.endX    = 0; this.endY    = 0;
        this.progress = 0; this.speed = 0.005;
        this.size    = 10; this.color = currentDataParticleColor;
        this.text    = ottomanCharacterPool[0];
        return;
      }
      this.active = true;
      const originRect  = originCard.getBoundingClientRect();
      const targetRect  = targetCard.getBoundingClientRect();
      this.startX    = originRect.left + originRect.width  * 0.5;
      this.startY    = originRect.top  + originRect.height * 0.2 + Math.random() * originRect.height * 0.6;
      this.endX      = targetRect.left + targetRect.width  * 0.5;
      this.endY      = targetRect.top  + targetRect.height * 0.2 + Math.random() * targetRect.height * 0.6;
      this.progress  = 0;
      this.speed     = 0.005 + Math.random() * 0.003;
      this.size      = 10 + Math.random() * 6;
      this.text      = ottomanCharacterPool[Math.floor(Math.random() * ottomanCharacterPool.length)];
      this.color     = currentDataParticleColor;
    }
    update() {
      if (!this.active) { this.reset(); return; }
      this.progress += this.speed; this.color = currentDataParticleColor;
      if (this.progress >= 1) this.reset();
    }
    draw() {
      if (!this.active || !dataFlowContext) return;
      const curX  = this.startX + (this.endX - this.startX) * this.progress;
      const curY  = this.startY + (this.endY - this.startY) * this.progress;
      let alpha = 0;
      if      (this.progress < 0.2) alpha = this.progress * 5;
      else if (this.progress > 0.8) alpha = (1 - this.progress) * 5;
      else                          alpha = 1;
      alpha *= 0.6;
      dataFlowContext.save();
      dataFlowContext.globalAlpha = alpha;
      dataFlowContext.fillStyle   = this.color;
      dataFlowContext.font        = `${this.size}px 'Noto Naskh Arabic',monospace`;
      dataFlowContext.fillText(this.text, curX, curY);
      dataFlowContext.restore();
    }
  }

  const startDataFlowAnimation = () => {
    if (isDataCanvasAnimating) return;
    isDataCanvasAnimating = true;
    dataParticles = [];
    for (let i = 0; i < 50; i++) dataParticles.push(new DataFlowParticle());
    tickDataFlowAnimation();
  };

  const stopDataFlowAnimation = () => {
    isDataCanvasAnimating = false;
    cancelAnimationFrame(dataCanvasAnimationFrameId);
    if (dataFlowContext) dataFlowContext.clearRect(0, 0, dataFlowCanvas.width, dataFlowCanvas.height);
  };

  const tickDataFlowAnimation = () => {
    if (!isDataCanvasAnimating || !dataFlowContext) return;
    dataFlowContext.clearRect(0, 0, dataFlowCanvas.width, dataFlowCanvas.height);
    dataParticles.forEach((p) => { p.update(); p.draw(); });
    dataCanvasAnimationFrameId = requestAnimationFrame(tickDataFlowAnimation);
  };

  /* -----------------------------------------------------------
     SCAN HIGHLIGHT REGIONS
     Percentage-based bounding boxes for each category layer,
     used to flash word regions as the scan line passes over them.
     ----------------------------------------------------------- */
  const scanHighlightRegions = {
    lexiqamusLayerEntry: [
      { top: 0, height: 8, left: 80, width: 18 }
    ],
    lexiqamusLayerSubentry: [
      { top:  3, height: 4, left: 10, width: 20 }, { top:  8, height: 4, left: 15, width: 18 },
      { top: 14, height: 4, left: 10, width: 22 }, { top: 19, height: 4, left: 12, width: 20 },
      { top: 25, height: 4, left: 15, width: 18 }, { top: 30, height: 4, left: 10, width: 20 },
      { top: 36, height: 4, left: 12, width: 18 }, { top: 41, height: 4, left: 15, width: 15 },
      { top: 47, height: 4, left: 10, width: 22 }, { top: 52, height: 4, left: 12, width: 20 },
      { top: 58, height: 4, left: 15, width: 18 }, { top: 63, height: 4, left: 10, width: 20 },
      { top: 69, height: 4, left: 12, width: 18 }, { top: 74, height: 4, left: 15, width: 15 },
      { top: 80, height: 4, left: 10, width: 22 }, { top: 85, height: 4, left: 12, width: 20 },
      { top: 90, height: 4, left: 10, width: 18 }, { top: 88, height: 5, left: 85, width: 12 },
      { top: 95, height: 5, left: 60, width: 10 }, { top: 95, height: 5, left: 70, width: 10 },
      { top: 95, height: 5, left: 80, width: 10 }, { top: 95, height: 5, left: 90, width:  8 }
    ],
    lexiqamusLayerRelated: [
      { top:  3, height: 4, left: 52, width: 13 }, { top:  8, height: 4, left: 68, width: 12 },
      { top: 38, height: 4, left: 85, width: 13 }, { top: 72, height: 4, left: 52, width: 15 },
      { top: 85, height: 4, left: 55, width: 12 }
    ]
  };

  const scanLineEl         = document.getElementById('lexiqamusScanLine');
  const scanBeamEl         = document.getElementById('lexiqamusScanBeam');
  const scanLabelEl        = document.getElementById('lexiqamusScanLabel');
  const scanFlashOverlayEl = document.getElementById('lexiqamusFlashOverlay');

  /* Builds the flash highlight <div>s for a given image layer before scanning. */
  const renderScanHighlightBoxes = (layerId, categoryColor) => {
    if (!scanFlashOverlayEl) return;
    scanFlashOverlayEl.innerHTML = '';
    const regions = scanHighlightRegions[layerId];
    if (!regions) return;
    const overlayHeight = scanFlashOverlayEl.offsetHeight;
    regions.forEach((region, index) => {
      const box           = document.createElement('div');
      box.className       = 'lexiqamus-scan-highlight-box';
      box.style.top       = `${region.top}%`;
      box.style.height    = `${region.height}%`;
      box.style.left      = `${region.left}%`;
      box.style.width     = `${region.width}%`;
      box.style.color     = categoryColor;
      box.dataset.yStartPx = String((region.top / 100) * overlayHeight);
      box.id              = `lexiqamusScanBox-${index}`;
      scanFlashOverlayEl.appendChild(box);
    });
  };

  /* Animates the scan line across one image layer, flashing word boxes as it passes. */
  const runCategoryScan = async (layerId, categoryColor, categoryLabel, blinkBeforeStart) => {
    currentDataParticleColor = categoryColor;

    if (scanBeamEl)  { scanBeamEl.style.background  = categoryColor; scanBeamEl.style.boxShadow = `0 0 15px ${categoryColor}`; }
    if (scanLabelEl) { scanLabelEl.style.background = categoryColor; scanLabelEl.style.color = 'white'; scanLabelEl.innerText = categoryLabel; }

    if (scanLineEl) {
      scanLineEl.style.opacity = '1';
      if (blinkBeforeStart) {
        scanLineEl.style.top = '0%';
        for (let b = 0; b < 4; b++) {
          scanLineEl.style.opacity = (b % 2 === 0) ? '1' : '0';
          await waitMilliseconds(150);
        }
        scanLineEl.style.opacity = '1';
        await waitMilliseconds(300);
      }
    }

    renderScanHighlightBoxes(layerId, categoryColor);
    const imageLayer     = document.getElementById(layerId);
    const highlightBoxes = Array.from(document.querySelectorAll('.lexiqamus-scan-highlight-box'));
    const scanDuration   = 2500;

    await new Promise((resolve) => {
      let startTime = null;
      const animateScanLine = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / scanDuration, 1);

        if (scanLineEl) scanLineEl.style.top = `${progress * 100}%`;
        if (imageLayer) imageLayer.style.clipPath = `inset(0 0 ${(1 - progress) * 100}% 0)`;

        if (scanFlashOverlayEl) {
          const scanYPosition = progress * scanFlashOverlayEl.offsetHeight;
          highlightBoxes.forEach((box) => {
            if (!box.classList.contains('lexiqamus-scan-flash-active')) {
              if (scanYPosition >= parseFloat(box.dataset.yStartPx) - 10) {
                box.classList.add('lexiqamus-scan-flash-active');
              }
            }
          });
        }

        if (progress < 1) {
          requestAnimationFrame(animateScanLine);
        } else {
          if (scanLineEl) scanLineEl.style.opacity = '0';
          resolve();
        }
      };
      requestAnimationFrame(animateScanLine);
    });
  };

  /* Positions a document card at the visual center of the stage (before animation begins). */
  const positionCardAtCenter = (card) => {
    card.style.width      = '300px';
    card.style.top        = '60%';
    card.style.left       = '50%';
    card.style.transition = 'none';
  };

  /* Animates a document card sliding into one of the resting spots on the stack. */
  const animateCardToDocumentSpot = (card, spotElementId, transitionDurationMs) => {
    const spot    = document.getElementById(spotElementId);
    const stageEl = document.getElementById('lexiqamusFlowStage');
    if (!spot || !stageEl) return;
    const spotRect      = spot.getBoundingClientRect();
    const stageRect     = stageEl.getBoundingClientRect();
    const rotationAngle = (spotElementId === 'lexiqamus-document-spot-original') ? '3deg' : '-6deg';
    card.style.transition = `all ${transitionDurationMs}ms cubic-bezier(0.25,0.8,0.25,1)`;
    card.style.width      = `${spotRect.width}px`;
    card.style.top        = `${spotRect.top  - stageRect.top}px`;
    card.style.left       = `${spotRect.left - stageRect.left}px`;
    card.style.transform  = `rotate(${rotationAngle})`;
  };

  /* Types text character-by-character into a DOM element, showing a blinking cursor. */
  const typewriterReveal = async (elementId, text, charIntervalMs) => {
    const el = document.getElementById(elementId);
    if (!el) return;
    el.innerHTML = '';
    const cursor       = document.createElement('span');
    cursor.className   = 'lexiqamus-typing-cursor';
    el.appendChild(cursor);
    for (let i = 0; i < text.length; i++) {
      cursor.insertAdjacentText('beforebegin', text.charAt(i));
      await waitMilliseconds(charIntervalMs);
    }
    cursor.remove();
  };

  /* Draws a curved SVG path between two pyramid nodes to represent a word relationship. */
  const drawPyramidConnectionPath = (sourceNodeId, targetNodeId, relationshipType) => {
    const pyramidArea = document.getElementById('lexiqamusPyramidArea');
    const pyramidSvg  = document.getElementById('lexiqamusPyramidSvg');
    const sourceNode  = document.getElementById(sourceNodeId);
    const targetNode  = document.getElementById(targetNodeId);
    if (!pyramidArea || !pyramidSvg || !sourceNode || !targetNode) return;

    const areaRect   = pyramidArea.getBoundingClientRect();
    const sourceRect = sourceNode.getBoundingClientRect();
    const targetRect = targetNode.getBoundingClientRect();
    const x1 = (sourceRect.left + sourceRect.width  / 2) - areaRect.left;
    const y1 =  sourceRect.bottom - areaRect.top - 2;
    const x2 = (targetRect.left + targetRect.width  / 2) - areaRect.left;
    const y2 =  targetRect.top  - areaRect.top;
    const controlPointY = y1 + (y2 - y1) * 0.9;
    const cssRelType    = (relationshipType === 'sub') ? 'subentry' : 'related';

    const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathEl.setAttribute('d', `M${x1},${y1} C${x1},${controlPointY} ${x2},${controlPointY} ${x2},${y2}`);
    pathEl.setAttribute('class', `lexiqamus-pyramid-connection-path lexiqamus-connection-path-${cssRelType}`);
    pyramidSvg.appendChild(pathEl);
  };

  /* Reveals the word relationship pyramid: main node typewriter, connection paths,
     mini subentry/related nodes, and category description boxes. */
  const animateWordRelationshipPyramid = async () => {
    const mainNode      = document.getElementById('lexiqamusPyramidMainNode');
    const transferOrb   = document.getElementById('lexiqamusTransferOrb');
    const processedCard = document.getElementById('lexiqamusCardProcessed');
    const descEntry     = document.getElementById('lexiqamusDescMain');
    const descSubentry  = document.getElementById('lexiqamusDescSub');
    const descRelated   = document.getElementById('lexiqamusDescRel');
    if (!mainNode || !processedCard) return;

    /* Animate the transfer orb flying from the processed card to the pyramid area */
    const processedWrapper = processedCard.querySelector('.lexiqamus-document-processed-wrapper');
    if (processedWrapper && transferOrb) {
      const wrapperRect = processedWrapper.getBoundingClientRect();
      const stageEl     = document.getElementById('lexiqamusFlowStage');
      const pyramidEl   = document.getElementById('lexiqamusPyramidArea');
      if (!stageEl || !pyramidEl) return;
      const stageRect   = stageEl.getBoundingClientRect();
      const pyramidRect = pyramidEl.getBoundingClientRect();
      transferOrb.style.left    = `${wrapperRect.left + wrapperRect.width * 0.85 - stageRect.left}px`;
      transferOrb.style.top     = `${wrapperRect.top  + wrapperRect.height * 0.04 - stageRect.top}px`;
      transferOrb.style.opacity = '1';
      transferOrb.classList.add('lexiqamus-transfer-orb-pulsing');
      await waitMilliseconds(1200);
      transferOrb.classList.remove('lexiqamus-transfer-orb-pulsing');
      transferOrb.style.transition = 'all 1s cubic-bezier(0.45,0,0.55,1)';
      transferOrb.style.left       = `${(pyramidRect.left - stageRect.left) + pyramidRect.width / 2}px`;
      transferOrb.style.top        = `${(pyramidRect.top  - stageRect.top)  + 30}px`;
      await waitMilliseconds(1000);
      transferOrb.style.opacity = '0';
    }

    /* Reveal main node with typewriter effect */
    mainNode.style.opacity   = '1';
    mainNode.style.transform = 'translateX(-50%) scale(1)';
    await waitMilliseconds(300);
    await typewriterReveal('lexiqamusMainOttoman', 'حركت', 150);
    await waitMilliseconds(200);
    await typewriterReveal('lexiqamusMainLatin', 'hareket', 80);
    await waitMilliseconds(500);

    /* Draw connection paths then reveal mini nodes */
    document.getElementById('lexiqamusPyramidSvg').innerHTML = '';
    ['lexiqamusMnSub1', 'lexiqamusMnSub2', 'lexiqamusMnSub3'].forEach((id) => {
      drawPyramidConnectionPath('lexiqamusPyramidMainNode', id, 'sub');
    });
    ['lexiqamusMnRel1', 'lexiqamusMnRel2', 'lexiqamusMnRel3', 'lexiqamusMnRel4'].forEach((id) => {
      drawPyramidConnectionPath('lexiqamusPyramidMainNode', id, 'rel');
    });
    const pyramidSvg = document.getElementById('lexiqamusPyramidSvg');
    if (pyramidSvg) pyramidSvg.style.clipPath = 'inset(0 0 0 0)';
    await waitMilliseconds(2500);

    document.querySelectorAll('#lexiqamusPyramidArea .lexiqamus-mini-node').forEach((node) => {
      node.style.clipPath = 'inset(0 0 0 0)';
    });
    await waitMilliseconds(3500); // combined: 1s for SVG reveal + 2.5s for nodes to settle

    /* Reveal category description boxes */
    if (descEntry)    { descEntry.style.opacity    = '1'; descEntry.style.transform    = 'translateX(20px)'; }
    await waitMilliseconds(1500);
    if (descSubentry) { descSubentry.style.opacity = '1'; descSubentry.style.transform = 'translateY(20px)'; }
    await waitMilliseconds(1500);
    if (descRelated)  { descRelated.style.opacity  = '1'; descRelated.style.transform  = 'translateY(20px)'; }
    await waitMilliseconds(500);
  };

  /* -----------------------------------------------------------
     FLOW SECTION MAIN SCENARIO
     Orchestrates the full document scanning animation sequence:
     1. Original document card flies in
     2. Document moves to resting spot
     3. Processed card appears with scan animation per category
     4. Word relationship pyramid builds out
     ----------------------------------------------------------- */
  const playFlowSectionScenario = async () => {
    const originalCard       = document.getElementById('lexiqamusCardOriginal');
    const processedCard      = document.getElementById('lexiqamusCardProcessed');
    const originalCardHeader = document.getElementById('lexiqamusOriginalCardHeader');

    if (!originalCard || !processedCard) {
      await waitMilliseconds(500);
      await animateWordRelationshipPyramid();
      return;
    }

    /* Step 1: Fly in the original document card from center */
    positionCardAtCenter(originalCard);
    originalCard.style.display = 'block';
    originalCard.style.opacity = '0';

    originalCard.style.transform = 'translate(-50%,-50%) scale(0.8)';
    if (originalCardHeader) { originalCardHeader.style.clipPath = 'inset(0 100% 0 0)'; originalCardHeader.style.transition = 'none'; }
    void originalCard.offsetWidth; // force reflow to enable transition
    originalCard.style.transition = 'all 1.5s cubic-bezier(0.2,0.8,0.2,1)';
    originalCard.style.opacity    = '1';
    originalCard.style.transform  = 'translate(-50%,-50%) scale(1)';
    if (originalCardHeader) {
      originalCardHeader.style.transition = 'clip-path 1.5s cubic-bezier(0.2,0.8,0.2,1)';
      originalCardHeader.style.clipPath   = 'inset(0 0 0 0)';
    }
    await waitMilliseconds(1600);

    /* Step 2: Slide original card to its resting spot */
    animateCardToDocumentSpot(originalCard, 'lexiqamus-document-spot-original', 1200);
    await waitMilliseconds(1000);

    /* Step 3: Show processed card and wipe in the base layer */
    processedCard.style.display    = 'block';
    processedCard.style.width      = originalCard.style.width;
    processedCard.style.top        = originalCard.style.top;
    processedCard.style.left       = originalCard.style.left;
    processedCard.style.transform  = originalCard.style.transform;
    processedCard.style.transition = 'none';
    processedCard.style.opacity    = '1';
    await waitMilliseconds(200);
    document.getElementById('lexiqamusLayerBase')?.classList.add('lexiqamus-wipe-in-left');
    await waitMilliseconds(1500);

    /* Step 4: Expand processed card to center and run category scans */
    processedCard.classList.add('lexiqamus-document-card-digital-ready');
    processedCard.style.transition = 'all 1.5s cubic-bezier(0.25,0.8,0.25,1)';
    processedCard.style.top        = '60%';
    processedCard.style.left       = '50%';
    processedCard.style.transform  = 'translate(-50%,-50%) scale(1.1) rotate(0deg)';
    startDataFlowAnimation();
    await waitMilliseconds(1500);

    const flowStage     = document.getElementById('lexiqamusFlowStage');
    const labelEntry    = flowStage?.dataset.scanLabelEntry || 'MAIN ENTRY';
    const labelSubentry = flowStage?.dataset.scanLabelSub   || 'SUB-ENTRIES';
    const labelRelated  = flowStage?.dataset.scanLabelRel   || 'RELATED CONCEPTS';

    await runCategoryScan('lexiqamusLayerEntry',    '#0d9488', labelEntry,    true);
    await waitMilliseconds(500);
    await runCategoryScan('lexiqamusLayerSubentry', '#d97706', labelSubentry, false);
    await waitMilliseconds(500);
    await runCategoryScan('lexiqamusLayerRelated',  '#2563eb', labelRelated,  false);
    await waitMilliseconds(1000);

    stopDataFlowAnimation();
    animateCardToDocumentSpot(processedCard, 'lexiqamus-document-spot-processed', 1500);
    await waitMilliseconds(1600);

    /* Step 5: Build out the word relationship pyramid */
    await animateWordRelationshipPyramid();

    /* Enable hover interaction on document cards */
    document.body.classList.add('lexiqamus-interaction-enabled');
    document.querySelectorAll('#lexiqamus-innovations-root .lexiqamus-active-document-card').forEach((card) => {
      card.addEventListener('mouseenter', () => {
        if (document.body.classList.contains('lexiqamus-interaction-enabled')) {
          document.body.classList.add('lexiqamus-card-hover-active');
        }
      });
      card.addEventListener('mouseleave', () => {
        if (document.body.classList.contains('lexiqamus-interaction-enabled')) {
          document.body.classList.remove('lexiqamus-card-hover-active');
        }
      });
    });
  };

}; // end initInnovationsLanding

/* =============================================================
   ENG DEFINITION MODE — hide innovations landing
   Called by search_output_controller when loading or clearing
   the English definitions iframe.
   ============================================================= */

// JS hooks for the scroll-down cue and innovations cards block (see _innovations_announcement_*.html.erb).
const INNOVATIONS_LANDING_SELECTORS = '.js-lexiqamus-scroll-indicator-wrapper, .js-lexiqamus-innovations-wrap'

// Collapses the innovations landing section and viewport when ENG definition search is active.
export const hideLexiqamusInnovationsLanding = () => {
  const pageRoot = window.searchOutput?.element // Lexiqamus search page root (.js-lexiqamus-search-results-root).
  if (!pageRoot) return // Not on a page with search-output; nothing to hide.

  const homeViewport = pageRoot.querySelector('.lexiqamus-home-viewport') // Pre-search hero block that wraps the scroll cue.
  if (homeViewport) {
    homeViewport.classList.add('lexiqamus-home-viewport--search-active') // ENG mode on; collapse full-screen hero height.
    pageRoot.closest('html')?.classList.add('lexiqamus-eng-definition-active')
    homeViewport.style.minHeight = '' // Clear JS viewport fill set by syncHomeViewportToWindowHeight.
    homeViewport.style.height = '' // Clear JS viewport fill set by syncHomeViewportToWindowHeight.
  }

  window.searchOutput?.releaseLandingPageScrollLock() // ENG iframe results need window scroll; OTT/decoder stay locked until Discover or keyboard overflow.

  const pageScope = pageRoot.parentElement // Innovations wrap renders as a sibling after pageRoot, not inside homeViewport.
  if (!pageScope) return

  pageScope.querySelectorAll(INNOVATIONS_LANDING_SELECTORS).forEach((el) => {
    el.hidden = true // Remove scroll cue and innovations block from layout.
    el.setAttribute('aria-hidden', 'true') // Hide from screen readers while ENG iframe results are shown.
  })

  pageRoot.closest('html')?.classList.remove('lexiqamus-innovations-scroll') // Turn off snap-scroll mode used on innovations landing.
  pageRoot.closest('body')?.classList.remove('lexiqamus-card-hover-active', 'lexiqamus-interaction-enabled') // Stop innovations card hover / interaction state.
}

// Exposed on window so controllers can call without importing (same pattern as showFlashMessage).
window.hideLexiqamusInnovationsLanding = hideLexiqamusInnovationsLanding

/* -------------------------------------------------------------
   University announcement modal — once per session, first 3 sessions only
   sessionStorage: hideEkualAnnouncement (this session)
   localStorage: ekualAnnouncementShowCount (sessions shown)
   ------------------------------------------------------------- */
// localStorage key: how many browser sessions have already shown the modal
const UNIVERSITY_ANNOUNCEMENT_SESSION_COUNT_KEY = 'ekualAnnouncementShowCount';
// sessionStorage key: modal was already shown in this tab (survives page reload)
const UNIVERSITY_ANNOUNCEMENT_SHOWN_SESSION_KEY = 'hideEkualAnnouncement';
// Stop showing the modal after this many sessions
const UNIVERSITY_ANNOUNCEMENT_MAX_SESSIONS = 3;

// Read how many sessions have already shown the modal (0 if never shown).
const readUniversityAnnouncementSessionCount = () => {
  try {
    const raw = window.localStorage.getItem(UNIVERSITY_ANNOUNCEMENT_SESSION_COUNT_KEY);
    const count = parseInt(raw, 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch (_e) {
    // ignore — storage may be unavailable (e.g. private browsing)
    return 0;
  }
};

// Save the updated session count to localStorage.
const writeUniversityAnnouncementSessionCount = (count) => {
  try {
    window.localStorage.setItem(UNIVERSITY_ANNOUNCEMENT_SESSION_COUNT_KEY, String(count));
  } catch (_e) {
    // ignore — storage may be unavailable
  }
};

// True when this tab already showed the modal (including after a reload).
const readUniversityAnnouncementShownThisSession = () => {
  try {
    return window.sessionStorage.getItem(UNIVERSITY_ANNOUNCEMENT_SHOWN_SESSION_KEY) === '1';
  } catch (_e) {
    // ignore — sessionStorage may be unavailable (e.g. private browsing)
    return false;
  }
};

const initUniversityAnnouncementModal = () => {
  const modalEl = document.getElementById('ekual-announcement-modal');
  if (!modalEl) return;
  // Skip if already shown this session, dismissed on this page, or init already ran.
  if (readUniversityAnnouncementShownThisSession() || window.universityAnnouncementDismissedThisVisit || window.universityAnnouncementModalInitialized) return;
  if (!window.bootstrap?.Modal) return;

  const sessionCount = readUniversityAnnouncementSessionCount();
  // After 3 sessions, never show the modal again.
  if (sessionCount >= UNIVERSITY_ANNOUNCEMENT_MAX_SESSIONS) return;

  window.universityAnnouncementModalInitialized = true;
  writeUniversityAnnouncementSessionCount(sessionCount + 1);
  try {
    // Remember for this tab so reloads do not show the modal again.
    window.sessionStorage.setItem(UNIVERSITY_ANNOUNCEMENT_SHOWN_SESSION_KEY, '1');
  } catch (_e) {
    // ignore — storage may be unavailable
  }

  // Prevent re-showing on Turbolinks navigation after the user closes the modal.
  modalEl.addEventListener('hidden.bs.modal', () => {
    window.universityAnnouncementDismissedThisVisit = true;
  }, { once: true });

  window.bootstrap.Modal.getOrCreateInstance(modalEl).show();
};

/* =============================================================
   BOOT — initialise on page load and after Turbolinks navigation
   ============================================================= */
document.addEventListener('DOMContentLoaded', () => {
  initInnovationsLanding();
  initUniversityAnnouncementModal();
});
document.addEventListener('turbolinks:load', () => {
  window.innovationsLandingInitialized = false;
  window.flowSectionScenarioStarted    = false;
  document.body.classList.remove('lexiqamus-card-hover-active', 'lexiqamus-interaction-enabled');
  const home = document.querySelector('.lexiqamus-home-viewport');
  if (home) delete home.dataset.lexiqamusHomeResizeBound;
  if (!document.getElementById('lexiqamus-innovations-root')) {
    document.documentElement.classList.remove('lexiqamus-innovations-scroll');
  }
  initInnovationsLanding();
  initUniversityAnnouncementModal();
});
